import type { NaiveDate } from "@tento-chrono";
import type { Option } from "@tento-lona/sheets";

/**
 * The read-only DOM face of a `lona-calendar-sheet-column`'s date binding:
 * the host attributes a bound column carries, so a page script or a browser
 * test can tell which dates a column paints without reaching into the
 * element's private props.
 *
 * - `data-du-type`: the unit the column spans, the bound partial date's
 *   `DateUnit` type (`"day"`, `"week"`, `"month"` or `"year"`).
 * - `data-date-start`: the first date the column paints, `yyyy-mm-dd`.
 * - `data-date-end`: the first date AFTER the column, `yyyy-mm-dd`; the range
 *   is half-open, `[data-date-start, data-date-end)`, so a day column of
 *   2026-09-15 reads `2026-09-15` / `2026-09-16`.
 *
 * Both dates are calendar dates in the column's own timezone (the zone of
 * its binding, which a personal-timezone column may not share with the
 * page); they carry no time of day. Dates are written as
 * `NaiveDate.rfc3339()`, so ISO strings of the same length compare in date
 * order. The attributes are set when the column is bound and removed when it
 * is recycled; an unbound column carries none of them.
 */
export const COLUMN_DATE_ATTRIBUTE_NAMES = [
  "data-du-type",
  "data-date-start",
  "data-date-end",
] as const;

export type ColumnDateAttributeName =
  (typeof COLUMN_DATE_ATTRIBUTE_NAMES)[number];

/** The values of {@link COLUMN_DATE_ATTRIBUTE_NAMES} for one binding. */
export type ColumnDateAttributes = Readonly<
  Record<ColumnDateAttributeName, string>
>;

/** The element side of the attribute write: an `Element` satisfies it. */
export interface ColumnDateAttributeHost {
  getAttribute(name: string): Option<string>;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/** The attribute values a column bound to `date` carries. */
export function columnDateAttributes(
  date: NaiveDate.Partial,
): ColumnDateAttributes {
  return Object.freeze({
    "data-du-type": date.type,
    "data-date-start": date.start.rfc3339(),
    "data-date-end": date.end.rfc3339(),
  });
}

/**
 * Brings `host`'s date attributes to `next` (`null` removes them), writing
 * only the attributes whose value differs: a rebind to the same dates, the
 * common case while a sheet scrolls, touches no attribute.
 */
export function writeColumnDateAttributes(
  host: ColumnDateAttributeHost,
  next: Option<ColumnDateAttributes>,
): void {
  for (const name of COLUMN_DATE_ATTRIBUTE_NAMES) {
    const value = next == null ? null : next[name];
    if (host.getAttribute(name) === value) continue;
    if (value == null) host.removeAttribute(name);
    else host.setAttribute(name, value);
  }
}
